import type {
  ChangeRequestReason,
  ChangeRequestResponse,
} from '@contracts/changeRequest/ChangeRequestContract';
import type { AddNoteRequest } from '@contracts/note/AddNoteRequest';
import type { OrderCategoriesTagsResponse } from '@contracts/order/OrderCategoriesTagsResponse';
import type { OrderCreationResponse } from '@contracts/order/OrderCreationResponse';
import type { OrderFiltersOptionsResponse } from '@contracts/order/OrderFiltersOptionsResponse';
import type {
  OrderListingResponse,
  OrderListResponse,
  OrdersPermitGroupsCountByCategory,
  Payment,
  PermitSummary as PermitSummaryResponse,
} from '@contracts/order/OrderListResponse';
import type { OrderPaginationResponse } from '@contracts/order/OrderPaginationResponse';
import type {
  OrderPermitApplicationGroupResponse,
  OrderPermitApplicationResponse,
  OrderResponse,
} from '@contracts/order/OrderResponse';
import type { OrderTransactionResponse } from '@contracts/order/OrderTransactionResponse';
import type { ReviewableOrderResponse } from '@contracts/order/ReviewableOrderResponse';
import type { BillingCurrency } from '@contracts/payment/Currency';
import { TransactionCurrency } from '@contracts/payment/Currency';
import type { PermitActions } from '@contracts/permit/PermitActions';
import { roundToTwoDecimalsString } from '@contracts/utils/RoundingAmount';
import { ChangeRequest } from '@src/changeRequest/domain/ChangeRequest';
import { ChangeRequestList } from '@src/changeRequest/domain/ChangeRequestList';
import { AuthenticatedClient } from '@src/common/services/AuthenticatedClient';
import { Timestamp } from '@src/common/time/Timestamp';
import type { ClientOrderSubmissionState } from '@src/orders/domain/ClientOrderSubmissionState';
import type { OrderStatus } from '@src/orders/domain/Order';
import { Order } from '@src/orders/domain/Order';
import { OrderList, OrderListing } from '@src/orders/domain/OrderList';
import { OrderTab, transformToOrderTab } from '@src/orders/domain/OrderTab';
import type { PermitStatus } from '@src/orders/domain/PermitApplication';
import { PermitSummary } from '@src/orders/domain/PermitSummary';
import { PreauthStatus } from '@src/orders/domain/PreauthStatus';
import type { ReviewableOrder } from '@src/orders/domain/ReviewableOrder';
import { Transaction } from '@src/orders/domain/Transaction';
import { OrderAlreadyClaimedException } from '@src/orders/exceptions/OrderAlreadyClaimedException';
import type { DuplicationTrigger } from '@src/orders/hooks/useDuplicatePermitAsNew';
import type { OrderPagination } from '@src/orders/hooks/useGetOrderPagination';
import type { SortBy } from '@src/orders/hooks/useOrdersWithFilters';
import type {
  AdditionalFee,
  FeesFormFields,
  FormPermitFees,
} from '@src/orders/schemas/ReviewOrderFormPermitFields';
import { ClientOrderSubmissionStateSchema } from '@src/orders/services/ClientOrderSubmissionStateSchema';
import { OrderCategorySchema } from '@src/orders/services/OrderCategorySchema';
import { parsePermitApplication } from '@src/permits/services/PermitApplicationClient';

import { OrderActionsSchema } from './OrderActionsSchema';
import { assembleReviewableOrder } from './ReviewableOrderAssembler';

type IdWithNote = {
  orderId: string;
  note: AddNoteRequest;
};

interface ListOrdersFilters {
  page: number;
  limit: number;
  assignedToMe: boolean;
  category: OrderTab;
  searchValue?: string;
  sortBy?: SortBy;
  status?: string;
  clientId?: string;
  assigneeId?: string;
}

interface OrderIdsFilter {
  orderIds: string[];
}

export const OrdersClient = {
  applyLastChangesToPermitApplication: async (
    orderId: string,
    permitId: string,
  ) => {
    await AuthenticatedClient.put<OrderPermitApplicationResponse>(
      `orders/${orderId}/permits/${permitId}:applyLastChanges`,
    );
  },
  getPermitApplicationWithChangesPreview: async ({
    orderId,
    permitId,
  }: {
    orderId: string;
    permitId: string;
  }) => {
    const { data: response } =
      await AuthenticatedClient.get<OrderPermitApplicationResponse>(
        `orders/${orderId}/permits/${permitId}:preview`,
      );
    return parsePermitApplication(response);
  },
  listWithFilters: async ({
    sortBy,
    ...filters
  }: ListOrdersFilters): Promise<OrderList> => {
    const { data: response } = await AuthenticatedClient.get<OrderListResponse>(
      'orders',
      {
        params: {
          orderBy: sortBy && `${sortBy.field}:${sortBy.direction}`,
          ...filters,
        },
      },
    );
    return parseOrderListResponse(response);
  },
  listWithFiltersPermitsCountByCategory: async ({
    orderIds,
  }: OrderIdsFilter): Promise<OrdersPermitGroupsCountByCategory[]> => {
    const { data } = await AuthenticatedClient.post<
      OrdersPermitGroupsCountByCategory[]
    >('/orders/permitGroupsCountByCategory', { orderIds });
    return data;
  },
  create: async (data: {
    clientId: string;
    billTo?: string;
  }): Promise<OrderCreationResponse> => {
    const { data: response } = await AuthenticatedClient.post<{
      orderId: string;
    }>('orders', data);
    return { orderId: response.orderId };
  },
  find: async (orderId: string) => {
    const { data: response } = await AuthenticatedClient.get<OrderResponse>(
      `/orders/${orderId}`,
    );

    const allowedActions = response.allowedActions.map((action) =>
      OrderActionsSchema.cast(action),
    );

    const changeRequests: ChangeRequestList | undefined =
      response.changeRequests
        ? assembleChangeRequests(response.changeRequests)
        : undefined;

    return new Order({
      id: response.id,
      displayId: response.displayId,
      clientId: response.clientId,
      clientName: response.clientName,
      doingBusinessAs: response.doingBusinessAs,
      claimedBy: response.claimedBy,
      status: response.status as OrderStatus,
      PONumber: response.PONumber,
      permitApplicationGroups: parseOrderPermitApplicationGroups(
        response.permitApplicationGroups,
      ),
      allowedActions: new Set(allowedActions),
      changeRequests,
    });
  },
  findOrderListing: async (orderId: string): Promise<OrderListing> => {
    const { data: response } =
      await AuthenticatedClient.get<OrderListingResponse>(
        `/orders/listing/${orderId}`,
      );
    return parseOrderListingResponse(response);
  },
  update: async (
    orderId: string,
    data: Record<string, unknown>,
    clientSubmissionState?: ClientOrderSubmissionState,
  ): Promise<void> => {
    if (clientSubmissionState) {
      await AuthenticatedClient.put(`client-orders/${orderId}`, {
        ...data,
        clientSubmissionState: clientSubmissionState.valueOf(),
      });
    } else {
      await AuthenticatedClient.put(`orders/${orderId}`, data);
    }
  },
  updateAsDraft: async (
    orderId: string,
    data: Record<string, unknown>,
  ): Promise<void> => {
    await AuthenticatedClient.post(`orders/${orderId}:saveAsDraft`, data);
  },
  findPaginationForOrder: async ({
    orderId,
    ...params
  }: {
    orderId: string;
    limit: number;
    assignedToMe: boolean;
  }): Promise<OrderPagination> => {
    const { data } = await AuthenticatedClient.get<OrderPaginationResponse>(
      `/pagination/orders/${orderId}`,
      {
        params,
      },
    );

    return {
      page: data.page,
      tab: transformToOrderTab(data.category),
    };
  },
  findReviewableOrder: async (orderId: string): Promise<ReviewableOrder> => {
    const { data: response } =
      await AuthenticatedClient.get<ReviewableOrderResponse>(
        `/reviewable/orders/${orderId}`,
      );
    return assembleReviewableOrder(response);
  },
  updateBillingInformation: async ({
    orderId,
    billTo,
  }: {
    orderId: string;
    billTo: string;
  }) => {
    const { data: response } =
      await AuthenticatedClient.post<ReviewableOrderResponse>(
        `reviewable/orders/${orderId}:updateBillingInformation`,
        { billToClientId: billTo },
      );

    return assembleReviewableOrder(response);
  },
  updateOrderBillTo: async ({
    orderId,
    billTo,
  }: {
    orderId: string;
    billTo: string;
  }): Promise<void> => {
    await AuthenticatedClient.post(
      `orders/${orderId}:updateBillingInformation`,
      { billToClientId: billTo },
    );
  },
  updateBillingCurrency: async ({
    orderId,
    billingCurrency,
  }: {
    orderId: string;
    billingCurrency: string;
  }) => {
    const { data: response } =
      await AuthenticatedClient.post<ReviewableOrderResponse>(
        `reviewable/orders/${orderId}:updateBillingCurrency`,
        { billingCurrency },
      );

    return assembleReviewableOrder(response);
  },
  updateFees: async ({
    orderId,
    fees,
  }: {
    orderId: string;
    fees: FeesFormFields;
  }) => {
    const permitFees = assemblePermitFeesRequest(fees.permitFees);
    const additionalFees = assembleAdditionalFeesRequest(fees.additionalFees);
    const { data: response } =
      await AuthenticatedClient.post<ReviewableOrderResponse>(
        `reviewable/orders/${orderId}:updateFees`,
        {
          permitFees,
          additionalFees,
        },
      );

    return assembleReviewableOrder(response);
  },
  addPermits: async (
    orderId: string,
    data: {
      permits: { typeId: string; quantity: number; typeOriginalId: string }[];
    },
  ): Promise<void> => {
    await AuthenticatedClient.post<OrderResponse>(
      `orders/${orderId}:updatePermitsQuantity`,
      data,
    );
  },
  duplicateOrder: async (
    orderId: string,
    data: { permitsToCopy: string[] },
  ): Promise<string> => {
    const { data: response } = await AuthenticatedClient.post(
      `orders/${orderId}/duplicate`,
      data,
    );
    return response.orderId;
  },
  changePermitsIndexing: async (
    orderId: string,
    data: {
      indexing: string[];
    },
  ): Promise<void> => {
    await AuthenticatedClient.post(
      `orders/${orderId}/permits:changeIndexing`,
      data,
    );
  },
  cancelPermit: async ({
    orderId,
    permitApplicationId,
    note,
  }: {
    orderId: string;
    permitApplicationId: string;
    note: AddNoteRequest;
  }) => {
    await AuthenticatedClient.post(
      `/orders/${orderId}/permits/${permitApplicationId}:cancel`,
      note,
    );
  },
  cancelDraftPermit: async ({
    orderId,
    permitApplicationId,
  }: {
    orderId: string;
    permitApplicationId: string;
  }) => {
    await AuthenticatedClient.post(
      `/orders/${orderId}/permits/${permitApplicationId}:cancel`,
    );
  },
  deleteOrder: async ({ orderId }: { orderId: string }) => {
    await AuthenticatedClient.delete(`/orders/${orderId}`);
  },
  putOnHold: async ({ orderId, note }: IdWithNote) => {
    await AuthenticatedClient.post(`/orders/${orderId}:putOnHold`, note);
  },
  revertFromOnHold: async ({ orderId, note }: IdWithNote) => {
    await AuthenticatedClient.post(`/orders/${orderId}:revertFromOnHold`, note);
  },
  reopen: async ({ orderId, note }: IdWithNote) => {
    await AuthenticatedClient.post(`orders/${orderId}:reopen`, note);
  },
  forceToReview: async ({ orderId }: { orderId: string }) => {
    await AuthenticatedClient.post(`orders/${orderId}:forceToReview`);
  },
  claimOrder: async (
    orderId: string,
    data: { userId: string },
  ): Promise<void> => {
    const response = await AuthenticatedClient.post(
      `orders/${orderId}:claim`,
      data,
    );

    if (response.status === 403) {
      throw new OrderAlreadyClaimedException();
    }
  },
  unclaimOrder: async (orderId: string): Promise<void> => {
    await AuthenticatedClient.post(`orders/${orderId}:unclaim`);
  },
  updateClient: async (
    orderId: string,
    data: { newClientId: string },
  ): Promise<void> => {
    await AuthenticatedClient.post(`orders/${orderId}:updateClient`, data);
  },
  claimPermit: async (
    orderId: string,
    permitId: string,
    data: { userId: string },
  ): Promise<void> => {
    await AuthenticatedClient.post(
      `orders/${orderId}/permits/${permitId}:claim`,
      data,
    );
  },
  unclaimPermit: async (orderId: string, permitId: string): Promise<void> => {
    await AuthenticatedClient.post(
      `orders/${orderId}/permits/${permitId}:unclaim`,
    );
  },
  cancel: async ({ orderId, note }: IdWithNote) => {
    await AuthenticatedClient.post(`orders/${orderId}:cancel`, note);
  },
  complete: async ({
    orderId,
    fields,
    billTo,
    billingCurrency,
  }: {
    orderId: string;
    fields: FeesFormFields;
    billTo: string;
    billingCurrency: BillingCurrency;
  }) => {
    const permitFeesAssembled = assemblePermitFeesRequest(fields.permitFees);
    const additionalFees = assembleAdditionalFeesRequest(fields.additionalFees);
    await AuthenticatedClient.post(`orders/${orderId}:complete`, {
      permitFees: permitFeesAssembled,
      additionalFees,
      billTo,
      billingCurrency,
    });
  },
  review: async ({
    orderId,
    fields,
  }: {
    orderId: string;
    fields: FeesFormFields;
  }) => {
    const permitFeesAssembled = assemblePermitFeesRequest(fields.permitFees);
    const additionalFees = assembleAdditionalFeesRequest(fields.additionalFees);
    await AuthenticatedClient.post(`orders/${orderId}:review`, {
      permitFees: permitFeesAssembled,
      additionalFees,
    });
  },
  getCategorizedTags: async ({
    assignedToMe,
  }: {
    assignedToMe: boolean;
  }): Promise<OrderCategoriesTagsResponse> => {
    const { data: response } =
      await AuthenticatedClient.get<OrderCategoriesTagsResponse>(
        'orders/categories/tags',
        {
          params: {
            assignedToMe,
          },
        },
      );
    return {
      [OrderTab.New]: response.new,
      [OrderTab.Active]: response.active,
      [OrderTab.Processed]: response.processed,
    };
  },
  getFiltersOptions: async (
    tab: OrderTab,
  ): Promise<OrderFiltersOptionsResponse> => {
    const { data: response } =
      await AuthenticatedClient.get<OrderFiltersOptionsResponse>(
        'orders/filters/options',
        {
          params: {
            category: tab,
          },
        },
      );
    return response;
  },
  getOrderTransactions: async (orderId: string): Promise<Transaction[]> => {
    const { data: response } = await AuthenticatedClient.get<
      OrderTransactionResponse[]
    >(`orders/${orderId}/transactions`);

    const assembleTransaction = (
      response: OrderTransactionResponse,
    ): Transaction => {
      return new Transaction({
        ...response,
        date: Timestamp.from(response.date),
        currency: TransactionCurrency[response.currency],
        children: response.children.map(assembleTransaction),
      });
    };

    return response.map(assembleTransaction);
  },
  generatePdf: async (orderId: string): Promise<void> => {
    await AuthenticatedClient.post(`orders/${orderId}:generatePdf`, orderId);
  },
};

export const parsePermitSummary = (response: PermitSummaryResponse) => {
  const { ...rest } = response;
  return new PermitSummary({
    ...rest,
    effectiveDate: Timestamp.from(response.effectiveDate),
    status: response.status as PermitStatus,
    statePermitDocument: response.statePermitDocument
      ? {
          documentId: response.statePermitDocument.id,
          key: response.statePermitDocument.key,
          sendToClient: response.statePermitDocument.sendToClient,
          showToClient: response.statePermitDocument.showToClient,
          displayName: response.statePermitDocument.displayName,
        }
      : undefined,
    trigger: response.trigger
      ? (response.trigger as DuplicationTrigger)
      : undefined,
    isSelfIssued: response.isSelfIssued,
    allowedActions: response.allowedActions as PermitActions[],
  });
};

const parseOrderListResponse = (response: OrderListResponse): OrderList => {
  const orders: OrderListing[] = response.orders.map((response) => {
    return parseOrderListingResponse(response);
  });

  return new OrderList(orders, response.totalOrders, response.totalPages);
};

const parseOrderListingResponse = (
  response: OrderListingResponse,
): OrderListing => {
  const status = response.status as OrderStatus;
  const permitSummaryGroups = response.permitSummary.permitSummaryGroups.map(
    ({ originalPermit, subsequentPermits }) => {
      return {
        originalPermit: parsePermitSummary(originalPermit),
        subsequentPermits: subsequentPermits.map((permit) =>
          parsePermitSummary(permit),
        ),
      };
    },
  );

  const allowedActions = response.allowedActions.map((action) =>
    OrderActionsSchema.cast(action),
  );

  return new OrderListing({
    ...response,
    status,
    category: OrderCategorySchema.cast(response.category),
    permitSummaryGroups,
    details: {
      ...response.details,
      effectiveDate: Timestamp.from(response.details.effectiveDate),
      submittedDate: Timestamp.from(response.details.submittedDate),
      payment: parsePayment(response.details.payment),
      PONumber: response.details.PONumber,
      changeRequests: response.details?.changeRequests
        ? assembleChangeRequests(response.details.changeRequests)
        : undefined,
    },
    createdAt: Timestamp.from(response.createdAt),
    updatedAt: Timestamp.from(response.updatedAt),
    allowedActions: new Set(allowedActions),
    permitCounts: response.permitSummary.counts,
    clientSubmissionState: response.clientSubmissionState
      ? ClientOrderSubmissionStateSchema.cast(response.clientSubmissionState)
      : undefined,
  });
};

const parseOrderPermitApplicationGroups = (
  groups: OrderPermitApplicationGroupResponse[],
) => {
  return groups.map((group) => ({
    originalPermit: parsePermitApplication(group.originalPermit),
    subsequentPermits: group.subsequentPermits.map((subsequent) =>
      parsePermitApplication(subsequent),
    ),
  }));
};

const parsePayment = ({ status, settledAmount, currency }: Payment) => {
  if (!Object.values(PreauthStatus).includes(status as PreauthStatus)) {
    throw Error(`could not parse payment status: ${status}`);
  }

  return {
    status: status as PreauthStatus,
    settledAmount,
    currency: currency ? TransactionCurrency[currency] : undefined,
  };
};

export const assemblePermitFeesRequest = (permitFees: FormPermitFees) =>
  Object.entries(permitFees).map(([permitId, fees]) => ({
    ...fees,
    jurisdictionFee: {
      ...fees.jurisdictionFee,
      amount: roundToTwoDecimalsString(Number(fees.jurisdictionFee.amount)),
    },
    serviceFee: {
      ...fees.serviceFee,
      amount: roundToTwoDecimalsString(Number(fees.serviceFee.amount)),
    },
    permitApplicationId: permitId,
  }));

export const assembleAdditionalFeesRequest = (
  additionalFees: AdditionalFee[],
) =>
  additionalFees.map((fee) => ({
    ...fee,
    taxable: fee.taxable,
    amount: roundToTwoDecimalsString(fee.amount),
  }));

const assembleChangeRequests = (
  changeRequestCollection: ChangeRequestResponse[],
): ChangeRequestList => {
  const changeRequestList: ChangeRequestList = new ChangeRequestList({
    changeRequests: [],
  });
  for (const changeRequestResponse of changeRequestCollection) {
    const changeRequest = new ChangeRequest({
      id: changeRequestResponse.id,
      orderId: changeRequestResponse.orderId,
      reason: changeRequestResponse.reason as ChangeRequestReason,
      form: changeRequestResponse.form,
      dirtyFields: changeRequestResponse.dirtyFields,
      requestedAt: new Date(changeRequestResponse.requestedAt),
    });
    changeRequestList.add(changeRequest);
  }
  return changeRequestList;
};
